/**
 * Facebook Pixel integration — only loads after user consent.
 *
 * Usage:
 *   import { initPixel, trackEvent, trackPageView } from '../lib/pixel/FacebookPixel'
 *
 *   // After consent granted:
 *   initPixel()
 *   trackPageView()
 *
 *   // On form submission:
 *   trackEvent('Lead')
 *   trackEvent('SubmitApplication')
 */

const PIXEL_ID = process.env.GATSBY_FB_PIXEL_ID

let isInitialized = false

declare global {
    interface Window {
        fbq: any
        _fbq: any
    }
}

/**
 * Injects the Facebook Pixel base script and initializes with the Pixel ID.
 * Does nothing if already initialized or if no Pixel ID is configured.
 */
export function initPixel(): void {
    if (typeof window === 'undefined') return
    if (isInitialized) return
    if (!PIXEL_ID) {
        console.warn('[FacebookPixel] No GATSBY_FB_PIXEL_ID configured.')
        return
    }

    // Facebook Pixel base code
    const f = window
    const b = document
    const n: any = (f.fbq = function (...args: any[]) {
        if (n.callMethod) {
            n.callMethod.apply(n, args)
        } else {
            n.queue.push(args)
        }
    })

    if (!f._fbq) f._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []

    const script = b.createElement('script')
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    const firstScript = b.getElementsByTagName('script')[0]
    firstScript.parentNode?.insertBefore(script, firstScript)

    window.fbq('init', PIXEL_ID)
    isInitialized = true
}

/**
 * Removes Facebook Pixel: clears cookies, removes script, resets state.
 * Called when user revokes marketing consent.
 */
export function revokePixel(): void {
    if (typeof window === 'undefined') return

    // Remove fbq from window
    delete window.fbq
    delete window._fbq
    isInitialized = false

    // Remove the injected script
    const scripts = document.querySelectorAll('script[src*="fbevents.js"]')
    scripts.forEach((s) => s.remove())

    // Remove Facebook cookies
    const fbCookies = document.cookie.split(';').filter((c) => c.trim().startsWith('_fb'))
    fbCookies.forEach((cookie) => {
        const name = cookie.split('=')[0].trim()
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    })
}

/**
 * Track a standard or custom event.
 */
export function trackEvent(eventName: string, params?: Record<string, any>): void {
    if (typeof window === 'undefined') return
    if (!isInitialized || !window.fbq) return

    if (params) {
        window.fbq('track', eventName, params)
    } else {
        window.fbq('track', eventName)
    }
}

/**
 * Track a PageView event.
 */
export function trackPageView(): void {
    trackEvent('PageView')
}
